import type { DefinitionFormData } from '../CampaignDefinitionStep';
import type { OfferFormData } from '../offers/offerForm';
import { nowDateTimeLocal, todayDate } from '../dateUtils';

// Mêmes règles que l'ancien BeOne (sfValidator*) : dates dans le futur, début <= fin, longueurs maximales.
// Les valeurs vides ne sont jamais en erreur ici : l'obligation de saisie est vérifiée par le back à la validation.
export type FieldErrors<K extends string> = Partial<Record<K, string>>;

export const OFFER_TITLE_MAX = 150;
export const OFFER_CODE_MAX = 40;
export const OFFER_DESCRIPTION_MAX = 500;
const MAX_EDITIONS = 99999;

const PAST_SEND = "La date d'envoi ne peut pas être antérieure à la date actuelle.";
const PAST_DATE = 'La date ne peut pas être antérieure à la date actuelle.';
const END_BEFORE_START = 'La date de fin ne peut pas être antérieure à la date de début.';

// Les saisies sont en « YYYY-MM-DD[THH:mm] » : la comparaison lexicographique suit l'ordre chronologique
const isBefore = (value: string, limit: string) => value !== '' && value < limit;

type DefinitionField = 'sendDate' | 'editionDateStart' | 'editionDateEnd' | 'followUpDateStart' | 'followUpDateEnd';

export const definitionErrors = (d: DefinitionFormData): FieldErrors<DefinitionField> => {
  const errors: FieldErrors<DefinitionField> = {};
  if (!d.isRecurring) {
    const now = nowDateTimeLocal();
    if (d.channel === 'cashdesk') {
      if (!d.isPreEdited) {
        if (isBefore(d.editionDateStart, now)) errors.editionDateStart = PAST_DATE;
        if (isBefore(d.editionDateEnd, now)) errors.editionDateEnd = PAST_DATE;
        else if (d.editionDateStart && isBefore(d.editionDateEnd, d.editionDateStart)) errors.editionDateEnd = END_BEFORE_START;
      }
    } else if (d.channel === 'courrier') {
      if (isBefore(d.sendDate.slice(0, 10), todayDate())) errors.sendDate = "La date de dépôt ne peut pas être antérieure à aujourd'hui.";
    } else if (isBefore(d.sendDate, now)) {
      errors.sendDate = PAST_SEND;
    }
  }
  if (!d.hasOffer && !d.isRecurring) {
    if (isBefore(d.followUpDateStart, todayDate())) errors.followUpDateStart = PAST_DATE;
    if (isBefore(d.followUpDateEnd, todayDate())) errors.followUpDateEnd = PAST_DATE;
    else if (isBefore(d.followUpDateEnd, d.followUpDateStart)) errors.followUpDateEnd = END_BEFORE_START;
  }
  return errors;
};

type OfferField =
  | 'title' | 'offerNumber' | 'description' | 'nbMaxUse' | 'nbMaxEdit' | 'startValidityDate' | 'endValidityDate';

const integerError = (value: string, min: number, max?: number): string | undefined => {
  if (value.trim() === '') return undefined;
  const n = Number(value);
  if (!Number.isInteger(n)) return 'Veuillez saisir un nombre entier.';
  if (n < min) return `La valeur minimale est de ${min}.`;
  if (max !== undefined && n > max) return `La valeur maximale est de ${max}.`;
  return undefined;
};

const tooLong = (value: string, max: number) => (value.length > max ? `La longueur maximale est de ${max} caractères.` : undefined);

export const offerErrors = (o: OfferFormData, { isRecurring, isCashdesk }: { isRecurring: boolean; isCashdesk: boolean }): FieldErrors<OfferField> => {
  const errors: FieldErrors<OfferField> = {
    title: tooLong(o.title, OFFER_TITLE_MAX),
    offerNumber: tooLong(o.offerNumber, OFFER_CODE_MAX),
    description: tooLong(o.description, OFFER_DESCRIPTION_MAX),
    nbMaxUse: integerError(o.nbMaxUse, 0),
    nbMaxEdit: isCashdesk ? integerError(o.nbMaxEdit, 0, MAX_EDITIONS) : undefined,
  };
  if (!isRecurring) {
    // Le début par défaut est « aujourd'hui 00:00 » : seule une journée entièrement passée est refusée
    if (isBefore(o.startValidityDate, `${todayDate()}T00:00`)) errors.startValidityDate = PAST_DATE;
    if (isBefore(o.endValidityDate, nowDateTimeLocal())) errors.endValidityDate = PAST_DATE;
    else if (isBefore(o.endValidityDate, o.startValidityDate)) errors.endValidityDate = END_BEFORE_START;
  }
  return Object.fromEntries(Object.entries(errors).filter(([, message]) => message)) as FieldErrors<OfferField>;
};

export const firstError = (errors: Record<string, string | undefined>): string | null =>
  Object.values(errors).find(message => message) ?? null;
